import React, { useState } from "react";
import { Tooltip } from "antd";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import AlertLimitUpgradeModal from "components/Alerts/AlertLimitUpgradeModal";
import styles from "./kpiView.module.scss";

const TONE_CLASS = {
  normal: "quotaFooterToneNormal",
  warning: "quotaFooterToneWarning",
  critical: "quotaFooterToneCritical",
};

function quotaTone(fillPercent) {
  if (fillPercent >= 90) return "critical";
  if (fillPercent >= 70) return "warning";
  return "normal";
}

export default function KpiQuotaFooter({
  used = 0,
  base = 25,
  addon = 0,
  orgQuota = null,
  canPurchase = false,
  collapsed = false,
  onToggleCollapse,
}) {
  const { t } = useTranslation();
  const history = useHistory();
  const [showUpgrade, setShowUpgrade] = useState(false);

  const total = base + addon;
  const remaining = Math.max(0, total - used);
  const fillPercent = total > 0 ? (used / total) * 100 : 0;
  const tone = quotaTone(fillPercent);
  const toneClass = styles[TONE_CLASS[tone]] || "";

  const breakdownTooltip =
    addon > 0
      ? t("team_kpi_view.95", "Temel {{base}} + {{addon}} add-on = {{total}} toplam", {
          base,
          addon,
          total,
        })
      : null;

  const handleUpgradeClick = () => {
    if (canPurchase && orgQuota?.can_purchase) {
      setShowUpgrade(true);
      return;
    }
    history.push("/settings/plan");
  };

  return (
    <>
      <div
        className={`${styles.quotaFooter} ${toneClass} ${
          collapsed ? styles.quotaFooterCollapsed : ""
        }`}
      >
        <button
          type="button"
          className={styles.quotaFooterCollapseBtn}
          onClick={onToggleCollapse}
          aria-expanded={!collapsed}
          aria-label={t("team_kpi_view.96", "Show or hide KPI quota")}
        >
          <span className={styles.quotaFooterTitle}>
            {t("team_kpi_view.94", "KPI quota")}
          </span>
          <span className={styles.quotaFooterChevron}>{collapsed ? "▲" : "▼"}</span>
        </button>

        {!collapsed && (
          <div className={styles.quotaFooterBody}>
            <Tooltip title={breakdownTooltip} placement="top">
              <div className={styles.quotaFooterBarRow}>
                <div
                  className={styles.quotaFooterBarTrack}
                  role="progressbar"
                  aria-valuenow={used}
                  aria-valuemin={0}
                  aria-valuemax={total}
                >
                  <div
                    className={styles.quotaFooterBarFill}
                    style={{ width: `${Math.min(100, fillPercent)}%` }}
                  />
                </div>
                <span className={styles.quotaFooterFraction}>
                  {used} / {total}
                </span>
              </div>
            </Tooltip>

            <p className={styles.quotaFooterRemaining}>
              {remaining > 0
                ? t("team_kpi_view.97", "{{count}} KPI slots remaining", {
                    count: remaining,
                  })
                : t("team_kpi_view.98", "Your KPI quota is full")}
            </p>

            <button
              type="button"
              className={styles.quotaFooterUpgrade}
              onClick={handleUpgradeClick}
            >
              {t("team_kpi_view.99", "Increase quota →")}
            </button>
          </div>
        )}
      </div>

      <AlertLimitUpgradeModal
        open={showUpgrade}
        onClose={() => setShowUpgrade(false)}
        limits={orgQuota}
        returnPath={window.location.pathname}
        source="kpi_view_footer"
      />
    </>
  );
}

export function kpiQuotaCreateDisabledTooltip(quotaLimitReached, t) {
  if (!quotaLimitReached) return null;
  return t("team_kpi_view.100", "You need to increase your quota");
}
